import { useCallback, useEffect, useRef, useState } from 'react';
import { ExternalLink, Fingerprint, RefreshCw, Search } from 'lucide-react';
import { api, ApiClientError } from './api';
import { Modal } from './Modal';
import type { IdentifyLogEntry, Notify } from './types';

type Props = {
  notify: Notify;
  /** 设备详情模式：只显示该设备的日志。 */
  deviceId?: string;
  /** 设备当前 Identify；null 表示尚未设置。 */
  deviceIdentify?: string | null;
  /** 全局模式：显示全部日志（含未关联设备），仅管理员可用。 */
  global?: boolean;
  /** 设备模式下的“去设置 Identify”入口。 */
  onEditIdentify?: () => void;
};

const PAGE_SIZE = 100;

function formatRateLimit(enabled: boolean, windowSeconds: number): string {
  if (!enabled) return '当前不限速（调试模式）';
  const hours = windowSeconds / 3600;
  if (Number.isInteger(hours) && hours >= 1) return `每个来源 IP 每 ${hours} 小时最多 1 条`;
  const minutes = Math.max(1, Math.round(windowSeconds / 60));
  return `每个来源 IP 每 ${minutes} 分钟最多 1 条`;
}

const errorText = (cause: unknown): string => (
  cause instanceof ApiClientError ? cause.message : cause instanceof Error ? cause.message : '操作失败'
);

function formatIdentifyTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

// portmux 心跳上报的日志形如 "p-49523-8081,s-4522"：p- 后是全部监听端口，
// ,s- 后是 SSH 上游端口。调试页 /__pm/ 挂在监听端口上，所以取第一个监听端口。
const PORTMUX_BEACON = /^p-(\d+(?:-\d+)*)(?:,s-\d+)?$/;

function portmuxDebugURL(message: string, ip: string, identify: string): string | null {
  if (!ip || !identify) return null;
  const match = PORTMUX_BEACON.exec(message.trim());
  if (!match) return null;
  const port = Number(match[1].split('-')[0]);
  if (!Number.isInteger(port) || port < 1 || port > 65535) return null;
  const host = ip.includes(':') ? `[${ip}]` : ip;
  return `http://${host}:${port}/__pm/?token=${encodeURIComponent(identify)}`;
}

export function IdentifyLogs({ notify, deviceId, deviceIdentify, global = false, onEditIdentify }: Props) {
  const [items, setItems] = useState<IdentifyLogEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [identifyInput, setIdentifyInput] = useState('');
  const [ipInput, setIpInput] = useState('');
  const [search, setSearch] = useState('');
  const [identify, setIdentify] = useState('');
  const [ip, setIp] = useState('');
  const [headersFor, setHeadersFor] = useState<IdentifyLogEntry | null>(null);
  const [rateLimitEnabled, setRateLimitEnabled] = useState(true);
  const [rateLimitWindowSeconds, setRateLimitWindowSeconds] = useState(7200);
  const generationRef = useRef(0);

  const apiBase = global
    ? '/api/identify-logs'
    : `/api/devices/${encodeURIComponent(deviceId ?? '')}/identify-logs`;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setIdentify(identifyInput.trim());
      setIp(ipInput.trim());
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput, identifyInput, ipInput]);

  const load = useCallback(async (before: number | null) => {
    const generation = ++generationRef.current;
    if (before === null) setLoading(true);
    else setLoadingMore(true);
    const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (search) params.set('search', search);
    if (global && identify) params.set('identify', identify);
    if (ip) params.set('ip', ip);
    if (before !== null) params.set('before', String(before));
    try {
      const data = await api<{
        items: IdentifyLogEntry[];
        nextCursor: number | null;
        rateLimitEnabled: boolean;
        rateLimitWindowSeconds: number;
      }>(`${apiBase}?${params.toString()}`);
      if (generation !== generationRef.current) return;
      setItems((current) => (before === null ? data.items : [...current, ...data.items]));
      setNextCursor(data.nextCursor ?? null);
      setRateLimitEnabled(data.rateLimitEnabled ?? true);
      setRateLimitWindowSeconds(data.rateLimitWindowSeconds || 7200);
      setError('');
    } catch (cause) {
      if (generation !== generationRef.current) return;
      setError(errorText(cause));
      notify('error', errorText(cause));
    } finally {
      if (generation === generationRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [apiBase, global, identify, ip, notify, search]);

  // Identify 变更后重新拉取，避免刚认领/释放时列表过期。
  useEffect(() => { void load(null); }, [load, deviceIdentify]);

  function clearFilters() {
    setSearchInput('');
    setIdentifyInput('');
    setIpInput('');
  }

  const filtersActive = Boolean(searchInput || ipInput || (global && identifyInput));
  const encodedIdentify = deviceIdentify ? encodeURIComponent(deviceIdentify) : '';
  const rateLimitText = formatRateLimit(rateLimitEnabled, rateLimitWindowSeconds);
  const headerEntries = headersFor
    ? Object.entries(headersFor.headers ?? {}).sort(([left], [right]) => left.localeCompare(right))
    : [];

  return (
    <section className="identify-log-page">
      <div className="section-head">
        <div>
          <h3>{global ? '全部 Identify 日志' : 'Identify 日志'}</h3>
          <p>
            {global
              ? `低权限写入入口：POST/GET /api/id/{identify}。未关联设备的记录也会保留在这里；${rateLimitText}。`
              : `通过 /api/id/{identify} 写入，无需设备 key。${rateLimitText}。`}
          </p>
        </div>
        <div className="section-actions">
          {!global && (
            <span className={`tag${deviceIdentify ? '' : ' muted'}`} title="当前设备 Identify">
              {deviceIdentify || '未设置 Identify'}
            </span>
          )}
          <span className={`status-pill ${rateLimitEnabled ? 'neutral' : 'warn'}`} title={rateLimitText}>
            {rateLimitEnabled ? '限速中' : '不限速'}
          </span>
          <span className="count-chip">{items.length}{nextCursor !== null ? '+' : ''} 条</span>
          <button type="button" className="secondary-button small" disabled={loading} onClick={() => void load(null)}>
            <RefreshCw className={loading ? 'spin' : ''} size={14} />刷新
          </button>
        </div>
      </div>

      {!global && !deviceIdentify && (
        <p className="identify-log-notice">
          该设备尚未设置 Identify，暂时无法通过 /api/id/ 写入。
          {onEditIdentify && <button type="button" className="text-button" onClick={onEditIdentify}>去设置</button>}
        </p>
      )}

      <div className="log-toolbar identify-log-toolbar">
        {global && (
          <label className="log-filter">Identify
            <input value={identifyInput} onChange={(event) => setIdentifyInput(event.target.value)} placeholder="精确匹配" aria-label="按 Identify 过滤" />
          </label>
        )}
        <label className="log-filter">来源 IP
          <input value={ipInput} onChange={(event) => setIpInput(event.target.value)} placeholder="精确匹配" aria-label="按来源 IP 过滤" />
        </label>
        <label className="log-filter grow">搜索
          <span className="search-box compact">
            <Search size={15} aria-hidden="true" />
            <input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="消息、Identify、设备名或 FQDN"
              aria-label="搜索 Identify 日志"
            />
          </span>
        </label>
        {filtersActive && <button type="button" className="text-button log-time-clear" onClick={clearFilters}>清除筛选</button>}
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}

      <div className="panel tenant-table-wrap" aria-busy={loading}>
        <div className={`tenant-table-head identify-log-grid${global ? ' global' : ''}`}>
          <span>时间</span>
          <span>来源 IP</span>
          {global && <span>Identify</span>}
          {global && <span>设备</span>}
          <span>方法</span>
          <span>消息</span>
          <span>请求头</span>
        </div>

        {loading && items.length === 0 && (
          <div className="panel-loader" role="status">正在加载 Identify 日志…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon"><Fingerprint size={22} /></span>
            <strong>还没有 Identify 日志</strong>
            <p>
              {global
                ? '任何通过 /api/id/{identify} 写入的日志都会出现在这里，包括尚未关联设备的记录。'
                : deviceIdentify
                  ? `用 POST /api/id/${encodedIdentify} 或 GET /api/id/${encodedIdentify}?log=... 写入第一条。`
                  : '先在上方“去设置”里给设备设置一个 Identify。'}
            </p>
          </div>
        )}

        {items.map((item) => {
          const debugURL = portmuxDebugURL(item.message, item.client_ip, item.identify);
          return (
            <div className={`tenant-table-row identify-log-grid identify-log-row${global ? ' global' : ''}`} key={item.id}>
              <time dateTime={item.created_at} title={formatIdentifyTime(item.created_at)}>{formatIdentifyTime(item.created_at)}</time>
              <span className="mono identify-log-ip" title={item.client_ip || '来源 IP 未知'}>{item.client_ip || '—'}</span>
              {global && <span className="mono identify-log-identify" title={item.identify}>{item.identify}</span>}
              {global && (
                <span className="identify-log-device" title={item.device_fqdn ?? item.device_name ?? '未关联设备'}>
                  {item.device_id
                    ? <>{item.device_name ?? '设备'}{item.device_fqdn ? <small>{item.device_fqdn}</small> : null}</>
                    : <span className="status-pill neutral">未关联</span>}
                </span>
              )}
              <span><span className="tag">{item.method}</span></span>
              <p className="identify-log-message">
                {item.message}
                {debugURL && (
                  <>
                    {' '}
                    <a
                      className="identify-log-debug-link"
                      href={debugURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`在新标签页打开 ${debugURL}`}
                    >打开调试页<ExternalLink size={11} aria-hidden="true" /></a>
                  </>
                )}
              </p>
              <span>
                <button
                  type="button"
                  className="text-button"
                  aria-label={`查看 ${item.identify} 在 ${formatIdentifyTime(item.created_at)} 的请求头`}
                  onClick={() => setHeadersFor(item)}
                >查看</button>
              </span>
            </div>
          );
        })}

        {nextCursor !== null && (
          <footer className="pagination-bar">
            <span>每次加载 {PAGE_SIZE} 条，按写入时间倒序</span>
            <button type="button" className="secondary-button small" disabled={loadingMore || loading} onClick={() => void load(nextCursor)}>
              {loadingMore ? '加载中…' : '加载更早'}
            </button>
          </footer>
        )}
      </div>

      {headersFor && (
        <Modal
          title="请求头"
          subtitle={`${headersFor.method} ${headersFor.path} · ${formatIdentifyTime(headersFor.created_at)}`}
          size="wide-modal"
          onClose={() => setHeadersFor(null)}
        >
          <div className="identify-header-meta">
            <div><span>来源 IP</span><strong className="mono">{headersFor.client_ip || '—'}</strong></div>
            <div><span>Identify</span><strong className="mono">{headersFor.identify}</strong></div>
            <div><span>关联设备</span><strong>{headersFor.device_name ?? '未关联'}</strong></div>
          </div>
          {headerEntries.length > 0
            ? (
              <div className="identify-header-list">
                {headerEntries.map(([name, value]) => (
                  <div key={name}><code>{name}</code><span>{value}</span></div>
                ))}
              </div>
            )
            : <p className="card-hint">该请求没有记录到请求头。</p>}
          <p className="card-hint">Authorization、Cookie、Token、API Key 等敏感头在落库前已替换为 [REDACTED]。</p>
        </Modal>
      )}
    </section>
  );
}
